// Reads every row of a query that can return more than the API's response cap.
//
// Production PostgREST returns at most 1,000 rows per request (max_rows in
// supabase/config.toml). A plain select on a table that has grown past that
// silently returns the first 1,000 and no error, which is how the dashboard
// came to read 1,000 of 1,494 claim lines (SEQ-240). Use this for any
// organisation-wide read of a table that can pass 1,000 rows.
//
// Keyset paging, not offset paging: each page asks for "rows with an id after
// the last id I saw". Ids are random uuids, so with offset paging a row
// inserted or deleted between two requests shifts every later page and a row is
// read twice or never. With a cursor, a row that existed before the read began
// can be neither repeated nor skipped.
//
// The caller builds one page: select must include "id", order by id ascending,
// apply .limit(limit), and when afterId is not null add .gt("id", afterId).

// Rows asked for per request. Below the 1,000 cap so a full page is never clipped.
export const FETCH_PAGE_SIZE = 500;

// Hard stop so a broken pager can never loop forever (500 x 200 = 100,000 rows).
const MAX_PAGES = 200;

export interface PageResult<T> {
  data: T[] | null;
  error: { message: string } | null;
}

// Pages through a query until a page comes back empty. The next page starts
// after the last id actually returned, so a server that clips pages below the
// requested size loses nothing. Any page error throws: a partial total that
// looks complete is worse than no total.
export async function fetchAllRows<T extends { id: string }>(
  fetchPage: (afterId: string | null, limit: number) => PromiseLike<PageResult<T>>,
  pageSize: number = FETCH_PAGE_SIZE,
): Promise<T[]> {
  const rows: T[] = [];
  let afterId: string | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const { data, error } = await fetchPage(afterId, pageSize);
    if (error) throw new Error(`Could not read all rows (page after ${afterId ?? "the start"}): ${error.message}`);
    if (!data || data.length === 0) return rows;
    rows.push(...data);
    afterId = data[data.length - 1].id;
  }
  throw new Error(`Could not read all rows: more than ${MAX_PAGES * pageSize} rows`);
}
